import { useEffect } from "react";
import { Map, Point } from "mapbox-gl";
import mapboxHelper from "utils/mapbox/mapboxHelper";
import arrayHelper from "utils/arrayHelper";
import { ParcelTool } from "types/ParcelTool";
import { GeoJSON } from "geojson";
import { CombineParcelsStartFunction } from "state/newDevelopment/types";

export const useDeveloperMapOnIdle = (
  map: React.MutableRefObject<Map | undefined>,
  boundingBox: [number, number, number, number] | null,
  setBoundingBox: (value: React.SetStateAction<[number, number, number, number] | null>) => void,
  updateUrl: () => void,
  pinPosition: [number, number] | null | undefined,
  selectedFeature: any,
  smartSearchIsOpen: boolean,
  selectParcelIfAvailable: (coordinates: any) => boolean,
  parcelPointToBeSelected: React.MutableRefObject<Point | null>,
  parcelTool: ParcelTool,
  getParcel: (point: Point) => null[] | (number[] | GeoJSON)[],
  combineParcelsStart: CombineParcelsStartFunction,
  selectedFeatureMembers: { [featureId: number]: GeoJSON }
) => {
  useEffect(() => {
    if (map.current) {
      const onIdle = () => {
        const currentBoundingBox = mapboxHelper.calculateCurrentBoundingBox(map.current);
        if (currentBoundingBox) {
          if (!boundingBox || !arrayHelper.equals(boundingBox, currentBoundingBox)) {
            setBoundingBox(currentBoundingBox);
            updateUrl();
          }
        }

        if (pinPosition && !selectedFeature && smartSearchIsOpen === false) {
          selectParcelIfAvailable(pinPosition);
        }

        if (parcelPointToBeSelected.current && parcelTool === ParcelTool.DrawParcel) {
          let [parcel, coordinates] = getParcel(parcelPointToBeSelected.current);
          if (parcel && "type" in parcel) {
            combineParcelsStart(parcel, coordinates as [number, number], selectedFeatureMembers);
            parcelPointToBeSelected.current = null;
          }
        }
      };

      map.current.on("idle", onIdle);

      return () => {
        if (map.current) {
          map.current.off("idle", onIdle);
        }
      };
    } else {
      return () => {}; // Return a no-op function when map.current is not set
    }
  }, [
    map,
    boundingBox,
    setBoundingBox,
    updateUrl,
    pinPosition,
    selectedFeature,
    smartSearchIsOpen,
    selectParcelIfAvailable,
    parcelPointToBeSelected,
    parcelTool,
    getParcel,
    combineParcelsStart,
    selectedFeatureMembers,
  ]);
};
